import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, User, Wallet, Save } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { CURRENCIES, money } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Settings() {
  const { user, updateProfile, formatApiErrorDetail } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [budget, setBudget] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      setCurrency(user.currency || "USD");
      setBudget(user.monthly_budget ? String(user.monthly_budget) : "");
    }
  }, [user]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name can't be empty"); return; }
    setSaving(true);
    try {
      await updateProfile({
        name: name.trim(), email, currency,
        monthly_budget: budget ? parseFloat(budget) : 0,
      });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl animate-fade-up">
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Settings</h1>
      <p className="text-sm text-slate-500">Update your profile, currency and budget</p>

      <div className="mt-6 space-y-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><User className="h-4 w-4 text-brand" />Profile</div>
          <div className="mt-4 space-y-4">
            <div>
              <Label>Name</Label>
              <Input data-testid="settings-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="settings-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Wallet className="h-4 w-4 text-brand" />Preferences</div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label>Default currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="settings-currency-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CURRENCIES).map(([code, sym]) => (
                    <SelectItem key={code} value={code}>{sym} {code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Monthly food budget</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{CURRENCIES[currency] || "$"}</span>
                <Input data-testid="settings-budget-input" type="number" step="0.01" value={budget}
                  onChange={(e) => setBudget(e.target.value)} placeholder="0.00" className="pl-8" />
              </div>
              <p className="mt-1 text-xs text-slate-400">Set to 0 to hide the budget tracker</p>
            </div>
          </div>
        </div>

        <Button onClick={save} disabled={saving} data-testid="settings-save-button"
          className="w-full rounded-full bg-brand py-6 text-base font-semibold hover:bg-brand-hover">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="mr-1.5 h-5 w-5" />Save changes</>}
        </Button>
      </div>
    </div>
  );
}
